export const getDefaultRange = (tab, locationType, rateViewMode) => {
  const isNation = locationType ? locationType.toLowerCase() === 'nation' : false;

  switch (tab) {
    case 'reports':
    case 'totals':
      return 'count';
    case 'trends':
      return 'trend_diff_pct';
    case 'rates':
      return isNation ? 'rate' : 'rate_vs_national_rate';
    case 'scores':
      return 'composite_score';
    default:
      return 'count';
  }
};

export const getMapRangeFromChartOption = (chartOption, activeTab, rateViewMode, stateAbbr) => {  
  if (activeTab === 'totals' || activeTab === 'reports') {
    if (chartOption === 'Total Crime') return 'count';
    if (chartOption.includes('Average') && !chartOption.includes('vs')) return 'count_avg';
    if (chartOption.includes('Avg Difference') && !chartOption.startsWith('%')) return 'avg_diff';
    if (chartOption.startsWith('% ') && chartOption.includes('Avg Difference')) return 'avg_diff_pct';
    if (chartOption.includes('Total Crime vs')) return 'count_vs_avg';
    if (chartOption.endsWith('Rank') && !chartOption.includes('KRIME')) return 'rank';
    if (chartOption.endsWith('Percentile') && !chartOption.includes('KRIME')) return 'percentile';
  }

  if (activeTab === 'trends') {
    if (chartOption === 'Total Crime') return 'count';
    if (chartOption === 'Previous Total') return 'prev_count';
    if (chartOption === 'Change from Previous Total') return 'trend_diff';
    if (chartOption === '% Change from Previous Total') return 'trend_diff_pct';
    if (chartOption === 'Total Crime vs Previous Total') return 'count_vs_prev';
    if (chartOption.endsWith('Rank') && !chartOption.includes('KRIME')) return 'rank';
    if (chartOption.endsWith('Percentile') && !chartOption.includes('KRIME')) return 'percentile';
  }

  if (activeTab === 'rates') {
    if (chartOption === 'Crime Rate') return 'rate';
    if (chartOption.endsWith('Rate') && !chartOption.startsWith('Crime')) return 'national_rate';
    if (chartOption.includes('Rate Difference') && !chartOption.startsWith('%')) return 'rate_diff';
    if (chartOption.startsWith('% ') && chartOption.includes('Rate Difference')) return 'rate_diff_pct';
    if (chartOption.startsWith('Crime Rate vs')) return 'rate_vs_national_rate';
    if (chartOption.endsWith('Rank') && !chartOption.includes('KRIME')) return 'rank';
    if (chartOption.endsWith('Percentile') && !chartOption.includes('KRIME')) return 'percentile';
  }

  if (activeTab === 'scores') {
    if (chartOption.includes('Score (Composite)')) return 'composite_score';
    if (chartOption.includes('Composite') && chartOption.includes('Rank')) return 'composite_score_rank';
    if (chartOption.includes('Score (Non-Weighted)')) return 'score';
    if (chartOption.includes('Rank') && !chartOption.includes('Composite')) return 'rank';
  }
  
  return getDefaultRange(activeTab);
};

export const getChartOptionFromMapRange = (range, locationType, selectedCrime, activeTab, crimeName, getActiveCompareMode, rateViewMode, stateAbbr) => {
  if (!range) return '';
  
  const locationTypeDisplay = locationType;
  
  const isCounty = locationType && locationType.toLowerCase() === 'county';
  const isCity = locationType && locationType.toLowerCase() === 'city';
  const isMetro = locationType && locationType.toLowerCase() === 'metro';
  const isCountyOrCity = isCounty || isCity;
  
  const compareMode = getActiveCompareMode();

  let comparisonPrefix = '';
  if (isCountyOrCity) {
    comparisonPrefix = compareMode === 'state' ? 
      (stateAbbr || '') : 'US';
  }

  if (activeTab === 'totals' || activeTab === 'reports') {
    switch(range) {
      case 'count': return 'Total Crime';
      case 'count_avg': return `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationTypeDisplay} Average`;
      case 'avg_diff': return `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationTypeDisplay} Avg Difference`;
      case 'avg_diff_pct': return `% ${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationTypeDisplay} Avg Difference`;
      case 'count_vs_avg': return `Total Crime vs ${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationTypeDisplay} Average`;
      case 'rank': return `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationTypeDisplay} Rank`;
      case 'percentile': return `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationTypeDisplay} Percentile`;
      default: return '';
    }
  }
  
  if (activeTab === 'trends') {
    switch(range) {
      case 'count': return 'Total Crime';
      case 'prev_count': return 'Previous Total';
      case 'trend_diff': return 'Change from Previous Total';
      case 'trend_diff_pct': return '% Change from Previous Total';
      case 'count_vs_prev': return 'Total Crime vs Previous Total';
      case 'rank': return `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationTypeDisplay} Rank`;
      case 'percentile': return `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationTypeDisplay} Percentile`;
      default: return '';
    }
  }
  
  if (activeTab === 'rates') {
    const getComparisonLabel = () => {
      if (isMetro) {
        return rateViewMode?.rateType === 'metro' ? 'Metro' : 'National';
      }
      if (isCountyOrCity) {
        if (rateViewMode.rateType === 'county' || rateViewMode.rateType === 'city') {
          const peerLocLabel = rateViewMode.rateType === 'county' ? 'County' : 'City';
          return rateViewMode.rateScope === 'state'
            ? `${stateAbbr} ${peerLocLabel}`
            : `US ${peerLocLabel}`;
        }
        return rateViewMode.rateScope === 'state'
          ? `${stateAbbr} State`
          : 'US National';
      }
      return 'National';
    };

    const compLabel = getComparisonLabel();
    
    switch(range) {
      case 'rate': return 'Crime Rate';
      case 'national_rate': return `${compLabel} Rate`;
      case 'rate_diff': return `${compLabel} Rate Difference`;
      case 'rate_diff_pct': return `% ${compLabel} Rate Difference`;
      case 'rate_vs_national_rate': return `Crime Rate vs ${compLabel} Rate`;
      case 'rank': return `${isCountyOrCity ? compLabel : locationType} Rank`;
      case 'percentile': return `${isCountyOrCity ? compLabel : locationType} Percentile`;
      default: return '';
    }
  }
  
  if (activeTab === 'scores') {
    switch(range) {
      case 'composite_score': return `${comparisonPrefix ? `${comparisonPrefix} ` : ''}KRIME Score (Composite)`;
      case 'composite_score_rank': return `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationTypeDisplay} Rank (Composite)`;
      case 'score': return `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${selectedCrime === 'all_crime' ? 'All ' : ''}${crimeName} Score (Non-Weighted)`;
      case 'rank': return `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationTypeDisplay} Rank (Non-Weighted)`;
      default: return '';
    }
  }
  
  return '';
};

export function resolveRatesChartMenuSelection({
  selectedChart,
  selectedChartOption,
  chartOptions,
  comparisonLabel,
  locationType,
  isCountyOrCity,
  isMetro,
}) {
  const pick = (s) => (s && chartOptions.includes(s) ? s : null);

  const rebuild = (title) => {
    if (!title || title === 'Crime Rate') return chartOptions[0] ?? 'Crime Rate';
    if (isMetro && (title.includes('Rank') || title.includes('Percentile'))) {
      return pick(title) ?? title;
    }
    if (title.includes('vs')) return `Crime Rate vs ${comparisonLabel} Rate`;
    if (title.startsWith('% ') && title.includes('Rate Difference')) {
      return `% ${comparisonLabel} Rate Difference`;
    }
    if (title.includes('Rate Difference') && !title.startsWith('%')) {
      return `${comparisonLabel} Rate Difference`;
    }
    if (title.endsWith(' Rate') && !title.startsWith('Crime')) {
      return `${comparisonLabel} Rate`;
    }
    const rankPctLabel = isCountyOrCity || isMetro ? comparisonLabel : locationType;
    if (title.includes('Rank')) return `${rankPctLabel} Rank`;
    if (title.includes('Percentile')) return `${rankPctLabel} Percentile`;
    return chartOptions[1] ?? chartOptions[0] ?? 'Crime Rate';
  };

  return (
    pick(selectedChart) ||
    pick(selectedChartOption) ||
    pick(rebuild(selectedChart || selectedChartOption || '')) ||
    chartOptions[0] ||
    'Crime Rate'
  );
}